import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
  Collapse,
  Paper,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  Edit,
  Save,
  Cancel,
  Lock,
  Visibility,
  VisibilityOff,
  VpnKey,
  AccountCircle,
  PhotoCamera,
  DeleteOutline,
  DeleteForever,
  Notifications,
} from '@mui/icons-material';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { maskCNPJ, maskCPF, maskPhoneBR, normalizeEmail, normalizeName, normalizeText } from '../../utils/inputMasks';
import { empresaService } from '../../services/empresaService';
import { getApiUrl } from '../../utils/runtimeConfig';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ComunicacaoInterna {
  id: number;
  empresa_id: number;
  empresa_nome: string;
  assunto: string;
  mensagem: string;
  status: string;
  enviado_em: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: { xs: 1.5, sm: 3 } }}>{children}</Box>}
  </div>
);

// ── Estilos compartilhados com AdminDashboard ────────────────────────────────
const cardSx = {
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(32,37,41,0.07)',
  border: '1px solid rgba(32,37,41,0.08)',
};

const UserProfile: React.FC = () => {
  const { state, updateProfile, clearError, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoSuccess, setLogoSuccess] = useState('');
  const [logoError, setLogoError] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const empresasVinculadas = useMemo(() => state.user?.empresas || [], [state.user?.empresas]);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(state.user?.empresa_id || empresasVinculadas[0]?.id || null);
  const [comunicacoes, setComunicacoes] = useState<ComunicacaoInterna[]>([]);

  const handleDeleteAccount = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError('');
      const token = localStorage.getItem('auth_token');
      const apiUrl = getApiUrl() || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      await axios.delete(`${apiUrl}/users/profile`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setDeleteDialogOpen(false);
      logout();
      navigate('/');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Erro ao excluir conta. Tente novamente mais tarde.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const validationSchema = Yup.object({
    nome: Yup.string().min(2, 'Nome deve ter pelo menos 2 caracteres').required('Nome é obrigatório'),
    email: Yup.string().email('Email inválido').required('Email é obrigatório'),
    telefone: Yup.string().matches(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, 'Formato: (11) 99999-9999'),
  });

  const formik = useFormik({
    initialValues: {
      nome: state.user?.nome || '',
      email: state.user?.email || '',
      telefone: state.user?.telefone || '',
      cpf: maskCPF(state.user?.cpf || ''),
      cnpj: maskCNPJ(state.user?.cnpj || ''),
      razao_social: state.user?.razao_social || '',
      nome_fantasia: state.user?.nome_fantasia || '',
      whatsapp: maskPhoneBR(state.user?.whatsapp || ''),
      servicos: state.user?.servicos || '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
      email_empresa: '',
      website: '',
      instagram: '',
      descricao_servico: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      await updateProfile({ ...values, empresa_id: selectedEmpresaId || state.user?.empresa_id });
      setIsEditing(false);
    },
  });

  // IMPORTANTE: formik não deve entrar nas deps — causaria loop infinito
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (state.user && empresasVinculadas.length > 0 && !selectedEmpresaId) {
      setSelectedEmpresaId(state.user.empresa_id || empresasVinculadas[0].id);
    }
  }, [state.user, empresasVinculadas, selectedEmpresaId]);

  const selectedEmpresa = empresasVinculadas.find((empresa) => empresa.id === selectedEmpresaId) || null;

  useEffect(() => {
    if (selectedEmpresa) {
      formik.setValues({
        ...formik.values,
        cnpj: maskCNPJ(selectedEmpresa.cnpj || ''),
        razao_social: selectedEmpresa.razao_social || '',
        nome_fantasia: selectedEmpresa.nome_fantasia || '',
        whatsapp: maskPhoneBR(selectedEmpresa.whatsapp || ''),
        servicos: selectedEmpresa.servicos || '',
        endereco: selectedEmpresa.endereco || '',
        numero: selectedEmpresa.numero || '',
        complemento: selectedEmpresa.complemento || '',
        bairro: selectedEmpresa.bairro || '',
        cidade: selectedEmpresa.cidade || '',
        estado: selectedEmpresa.estado || '',
        cep: selectedEmpresa.cep || '',
        email_empresa: selectedEmpresa.email_empresa || '',
        website: selectedEmpresa.website || '',
        instagram: selectedEmpresa.instagram || '',
        descricao_servico: selectedEmpresa.descricao_servico || '',
      });
      setLogoPreview(null);
      setLogoSuccess('');
      setLogoError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmpresaId]);

  useEffect(() => {
    if (state.user) {
      formik.resetForm({
        values: {
          nome: state.user.nome || '',
          email: state.user.email || '',
          telefone: state.user.telefone || '',
          cpf: maskCPF(state.user.cpf || ''),
          cnpj: maskCNPJ(selectedEmpresa?.cnpj || state.user.cnpj || ''),
          razao_social: selectedEmpresa?.razao_social || state.user.razao_social || '',
          nome_fantasia: selectedEmpresa?.nome_fantasia || state.user.nome_fantasia || '',
          whatsapp: maskPhoneBR(selectedEmpresa?.whatsapp || state.user.whatsapp || ''),
          servicos: selectedEmpresa?.servicos || state.user.servicos || '',
          endereco: selectedEmpresa?.endereco || '',
          numero: selectedEmpresa?.numero || '',
          complemento: selectedEmpresa?.complemento || '',
          bairro: selectedEmpresa?.bairro || '',
          cidade: selectedEmpresa?.cidade || '',
          estado: selectedEmpresa?.estado || '',
          cep: selectedEmpresa?.cep || '',
          email_empresa: selectedEmpresa?.email_empresa || '',
          website: selectedEmpresa?.website || '',
          instagram: selectedEmpresa?.instagram || '',
          descricao_servico: selectedEmpresa?.descricao_servico || '',
        },
      });
    }
  }, [state.user, selectedEmpresa]); // apenas dados carregados

  useEffect(() => {
    if (state.user?.tipo !== 'empresa') {
      setComunicacoes([]);
      return;
    }
    const token = localStorage.getItem('auth_token');
    axios.get(`${getApiUrl() || process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/comunicacoes`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }).then((response) => {
      setComunicacoes(Array.isArray(response.data?.data) ? response.data.data : []);
    }).catch(() => setComunicacoes([]));
  }, [state.user?.id, state.user?.tipo]);

  const marcarComunicacaoComoLida = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    try {
      await axios.put(`${getApiUrl() || process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/comunicacoes/${id}/lida`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setComunicacoes((atual) => atual.map((comunicacao) => comunicacao.id === id ? { ...comunicacao, status: 'lido' } : comunicacao));
    } catch {
      setLogoError('Não foi possível atualizar a mensagem.');
    }
  };

  const handleEdit = () => { setIsEditing(true); clearError(); };
  const handleCancel = () => { setIsEditing(false); formik.resetForm(); clearError(); };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError('');
    setLogoSuccess('');
    // preview local
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    // upload
    setLogoUploading(true);
    try {
      const resp: any = await empresaService.uploadLogo(file);
      const url = resp?.url || resp?.data?.url || resp?.logo_url || resp?.path;
      if (url) {
        await updateProfile({ logo_url: url });
        setLogoSuccess('Logo atualizada com sucesso!');
        setTimeout(() => setLogoSuccess(''), 3000);
      } else {
        setLogoError('Não foi possível obter a URL da logo.');
      }
    } catch {
      setLogoError('Erro ao fazer upload da logo.');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleFormattedChange = (field: string, value: string) => {
    let formatted = value;
    switch (field) {
      case 'nome': case 'razao_social': case 'nome_fantasia': formatted = normalizeName(value); break;
      case 'email': case 'email_empresa': formatted = normalizeEmail(value); break;
      case 'telefone': case 'whatsapp': formatted = maskPhoneBR(value); break;
      case 'cpf': formatted = maskCPF(value); break;
      case 'cnpj': formatted = maskCNPJ(value); break;
      case 'website': case 'instagram': formatted = value.replace(/\s+/g, '').toLowerCase(); break;
      case 'endereco': case 'complemento': case 'bairro': case 'cidade': formatted = normalizeText(value); break;
      case 'estado': formatted = value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2); break;
    }
    formik.setFieldValue(field, formatted);
  };

  const passwordFormik = useFormik({
    initialValues: { senha_atual: '', nova_senha: '', confirmar_senha: '' },
    validationSchema: Yup.object({
      senha_atual: Yup.string().required('Senha atual é obrigatória'),
      nova_senha: Yup.string().min(6, 'Mínimo 6 caracteres').required('Nova senha é obrigatória'),
      confirmar_senha: Yup.string()
        .oneOf([Yup.ref('nova_senha')], 'As senhas não coincidem')
        .required('Confirmação é obrigatória'),
    }),
    onSubmit: async (values, helpers) => {
      setPasswordError('');
      setPasswordSuccess('');
      const response = await authService.changePassword({
        senha_atual: values.senha_atual,
        nova_senha: values.nova_senha,
      });
      if (response.success) {
        setPasswordSuccess('Senha alterada com sucesso!');
        helpers.resetForm();
        setTimeout(() => { setShowPasswordSection(false); setPasswordSuccess(''); }, 2000);
      } else {
        setPasswordError(response.message || 'Erro ao alterar senha');
      }
    },
  });

  useEffect(() => {
    if (state.user?.tipo === 'empresa' && new URLSearchParams(location.search).get('aba') === 'mensagens') setTabValue(2);
  }, [state.user?.tipo, location.search]);

  if (!state.user) {
    return (
      <Alert severity="warning">Você precisa estar logado para acessar seu perfil.</Alert>
    );
  }

  const getInitials = (name: string) =>
    name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const getUserTypeLabel = (tipo: string) => {
    switch (tipo) {
      case 'admin': return 'Administrador';
      case 'empresa': return 'Empresa';
      default: return 'Usuário';
    }
  };

  const getStatusColor = (tipo: string) => {
    switch (tipo) {
      case 'admin': return '#C23535';
      case 'empresa': return '#1565c0';
      default: return '#2e7d32';
    }
  };

  const profileDisplayName = state.user.tipo === 'empresa'
    ? state.user.nome_fantasia || state.user.razao_social || state.user.nome || 'Empresa'
    : state.user.nome || 'Usuário';
  const memberSinceValue = state.user.criada_em || (state.user as any).criado_em;
  const memberSince = memberSinceValue && !Number.isNaN(new Date(memberSinceValue).getTime())
    ? new Date(memberSinceValue).toLocaleDateString('pt-BR')
    : 'data não informada';

  const isEmpresa = state.user.tipo === 'empresa';
  const profileLogoUrl = isEmpresa ? selectedEmpresa?.logo_url || state.user.logo_url : '';
  const tabsConfig = isEmpresa
    ? [
        { label: 'Dados Pessoais', icon: <AccountCircle /> },
        { label: 'Empresa', icon: <Business /> },
        { label: 'Mensagens', icon: <Notifications /> },
        { label: 'Segurança', icon: <Lock /> },
      ]
    : [
        { label: 'Dados Pessoais', icon: <AccountCircle /> },
        { label: 'Segurança', icon: <Lock /> },
      ];

  const securityTabIndex = isEmpresa ? 3 : 1;

  return (
    <Box>
      {/* ── Painel com abas ── */}
      <Paper sx={{ borderRadius: '8px', boxShadow: '0 8px 30px rgba(32,37,41,0.08)', overflow: 'hidden', border: '1px solid rgba(32,37,41,0.08)' }}>
        {/* Avatar + Info no topo */}
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              width: { xs: 92, sm: 88 },
              height: { xs: 92, sm: 88 },
              borderRadius: 1.5,
              border: '1px solid rgba(32,37,41,0.12)',
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1,
              overflow: 'hidden',
              flex: '0 0 auto',
              boxShadow: '0 8px 22px rgba(32,37,41,0.08)',
            }}
          >
            {profileLogoUrl ? (
              <Box
                component="img"
                src={profileLogoUrl}
                alt="Logo da empresa"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : state.user.tipo === 'empresa' ? (
              <Business sx={{ fontSize: 36, color: '#C23535' }} />
            ) : (
              <Typography fontWeight={800} fontSize={28} color="#C23535">{getInitials(profileDisplayName)}</Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700}>
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>Nome: </Box>
              {profileDisplayName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              <Chip
                label={getUserTypeLabel(state.user.tipo)}
                size="small"
                sx={{
                  bgcolor: getStatusColor(state.user.tipo),
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Membro desde: {memberSince}
              </Typography>
            </Box>
          </Box>
          {!isEditing ? (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={handleEdit}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, borderColor: '#C23535', color: '#C23535', '&:hover': { borderColor: '#A52A2A', bgcolor: 'rgba(194,53,53,0.05)' }, borderRadius: 2, fontWeight: 600 }}
            >
              Editar Perfil
            </Button>
          ) : (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, width: { xs: '100%', sm: 'auto' }, '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={() => formik.handleSubmit()}
                disabled={state.loading}
                sx={{ bgcolor: '#C23535', '&:hover': { bgcolor: '#A52A2A' }, borderRadius: 2, fontWeight: 700 }}
              >
                {state.loading ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                sx={{ borderRadius: 2 }}
              >
                Cancelar
              </Button>
            </Box>
          )}
        </Box>

        {/* Alerts */}
        {state.error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={clearError}>{state.error}</Alert>
          </Box>
        )}

        {isEmpresa && comunicacoes.length > 0 && (
          <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 2 }}>
            <Card sx={{ ...cardSx, borderLeft: '4px solid #C23535' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Notifications sx={{ color: '#C23535' }} />
                  <Typography fontWeight={700}>Comunicações da IDEBRASIL</Typography>
                </Box>
                {comunicacoes.slice(0, 3).map((comunicacao) => (
                  <Box key={comunicacao.id} sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={700}>{comunicacao.assunto}</Typography>
                    <Typography variant="body2" color="text.secondary">{comunicacao.mensagem}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root.Mui-selected': { color: '#C23535', fontWeight: 700 },
            '& .MuiTabs-indicator': { bgcolor: '#C23535' },
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabsConfig.map((t) => (
            <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>

        {/* ── ABA: Dados Pessoais ── */}
        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={cardSx}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 2 }}>
                      Dados Pessoais
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" id="nome" name="nome" label="Nome Completo"
                          value={formik.values.nome}
                          onChange={(e) => handleFormattedChange('nome', e.target.value)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.nome && Boolean(formik.errors.nome)}
                          helperText={formik.touched.nome && formik.errors.nome}
                          disabled={!isEditing}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" id="email" name="email" label="Email" type="email"
                          value={formik.values.email}
                          onChange={(e) => handleFormattedChange('email', e.target.value)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.email && Boolean(formik.errors.email)}
                          helperText={formik.touched.email && formik.errors.email}
                          disabled={!isEditing}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" id="telefone" name="telefone" label="Telefone"
                          value={formik.values.telefone}
                          onChange={(e) => handleFormattedChange('telefone', e.target.value)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.telefone && Boolean(formik.errors.telefone)}
                          helperText={formik.touched.telefone && formik.errors.telefone}
                          disabled={!isEditing}
                          placeholder="(11) 99999-9999"
                          InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth size="small" id="cpf" name="cpf" label="CPF"
                          value={formik.values.cpf}
                          onChange={(e) => handleFormattedChange('cpf', e.target.value)}
                          onBlur={formik.handleBlur}
                          error={formik.touched.cpf && Boolean(formik.errors.cpf)}
                          helperText={formik.touched.cpf && formik.errors.cpf}
                          disabled={!isEditing}
                          placeholder="123.456.789-01"
                          InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={cardSx}>
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#C23535' }}>
                      Edição dos dados
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Atualize nome, email, telefone e dados da empresa quando necessário.
                    </Typography>
                    <Button
                      variant={isEditing ? 'contained' : 'outlined'}
                      startIcon={isEditing ? <Save /> : <Edit />}
                      onClick={() => isEditing ? formik.handleSubmit() : handleEdit()}
                      disabled={state.loading}
                      sx={{
                        mt: 1,
                        bgcolor: isEditing ? '#C23535' : 'transparent',
                        color: isEditing ? '#fff' : '#C23535',
                        borderColor: '#C23535',
                        '&:hover': { bgcolor: isEditing ? '#A52A2A' : 'rgba(194,53,53,0.05)' },
                        borderRadius: 2,
                        fontWeight: 700,
                      }}
                    >
                      {isEditing ? 'Salvar alterações' : 'Editar informações'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* ── ABA: Empresa (somente tipo=empresa) ── */}
        {isEmpresa && (
          <TabPanel value={tabValue} index={1}>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>


                {empresasVinculadas.length > 1 && (
                  <Grid item xs={12}>
                    <Card sx={{ ...cardSx, borderLeft: '4px solid #C23535' }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 1 }}>
                          Empresas vinculadas ao CPF
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Selecione qual empresa deseja gerenciar neste painel.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {empresasVinculadas.map((empresa) => (
                            <Chip
                              key={empresa.id}
                              clickable
                              color={selectedEmpresaId === empresa.id ? 'primary' : 'default'}
                              variant={selectedEmpresaId === empresa.id ? 'filled' : 'outlined'}
                              label={empresa.nome_fantasia || empresa.razao_social || 'Empresa'}
                              onClick={() => setSelectedEmpresaId(empresa.id)}
                              sx={{ maxWidth: '100%', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Card de logo */}
                <Grid item xs={12} md={4}>
                  <Card sx={cardSx}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 2 }}>
                        Logo da Empresa
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: 220,
                            height: 132,
                            bgcolor: '#f4f4f5',
                            border: '2px dashed #C23535',
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1.5,
                          }}
                        >
                          {logoPreview || selectedEmpresa?.logo_url || state.user.logo_url ? (
                            <Box
                              component="img"
                              src={logoPreview || selectedEmpresa?.logo_url || state.user.logo_url || undefined}
                              alt="Logo da empresa"
                              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <Business sx={{ fontSize: 48, color: '#C23535' }} />
                          )}
                          {logoUploading && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.75)', borderRadius: 3 }}>
                              <CircularProgress size={32} sx={{ color: '#C23535' }} />
                            </Box>
                          )}
                        </Box>
                        {logoSuccess && <Alert severity="success" sx={{ width: '100%', py: 0.5 }}>{logoSuccess}</Alert>}
                        {logoError && <Alert severity="error" sx={{ width: '100%', py: 0.5 }}>{logoError}</Alert>}
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleLogoChange}
                        />
                        <Button
                          type="button"
                          variant="contained"
                          startIcon={<PhotoCamera />}
                          onClick={() => logoInputRef.current?.click()}
                          disabled={logoUploading}
                          size="small"
                          sx={{ bgcolor: '#C23535', '&:hover': { bgcolor: '#A52A2A' }, borderRadius: 2, fontWeight: 700 }}
                        >
                          {logoUploading ? 'Enviando...' : 'Alterar Logo'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                          JPG, PNG ou WebP · máx. 5 MB
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Card sx={cardSx}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 2 }}>
                        Dados da Empresa
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="Razão Social"
                            value={formik.values.razao_social}
                            onChange={(e) => handleFormattedChange('razao_social', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="Nome Fantasia"
                            value={formik.values.nome_fantasia}
                            onChange={(e) => handleFormattedChange('nome_fantasia', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="CNPJ"
                            value={formik.values.cnpj}
                            onChange={(e) => handleFormattedChange('cnpj', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            placeholder="12.345.678/0001-90"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Business /></InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="WhatsApp da Empresa"
                            value={formik.values.whatsapp}
                            onChange={(e) => handleFormattedChange('whatsapp', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            placeholder="(45) 99999-9999"
                            InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="Email da Empresa"
                            value={formik.values.email_empresa}
                            onChange={(e) => handleFormattedChange('email_empresa', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="Website"
                            value={formik.values.website}
                            onChange={(e) => handleFormattedChange('website', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="Instagram"
                            value={formik.values.instagram}
                            onChange={(e) => handleFormattedChange('instagram', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Endereço completo
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="Endereço"
                            value={formik.values.endereco}
                            onChange={(e) => handleFormattedChange('endereco', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={5} md={2}>
                          <TextField
                            fullWidth size="small" label="Número"
                            value={formik.values.numero}
                            onChange={(e) => formik.setFieldValue('numero', e.target.value.replace(/[^0-9A-Za-z\-/ ]/g, '').slice(0, 12))}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={7} md={4}>
                          <TextField
                            fullWidth size="small" label="Complemento"
                            value={formik.values.complemento}
                            onChange={(e) => handleFormattedChange('complemento', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth size="small" label="Bairro"
                            value={formik.values.bairro}
                            onChange={(e) => handleFormattedChange('bairro', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth size="small" label="Cidade"
                            value={formik.values.cidade}
                            onChange={(e) => handleFormattedChange('cidade', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={5} md={2}>
                          <TextField
                            fullWidth size="small" label="UF"
                            value={formik.values.estado}
                            onChange={(e) => handleFormattedChange('estado', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={7} md={2}>
                          <TextField
                            fullWidth size="small" label="CEP"
                            value={formik.values.cep}
                            onChange={(e) => formik.setFieldValue('cep', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" multiline minRows={4} label="Descrição da Empresa"
                            value={formik.values.descricao_servico}
                            onChange={(e) => formik.setFieldValue('descricao_servico', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            placeholder="Descreva a empresa"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" multiline minRows={4} label="Serviços oferecidos"
                            value={formik.values.servicos}
                            onChange={(e) => formik.setFieldValue('servicos', e.target.value)}
                            onBlur={formik.handleBlur}
                            disabled={!isEditing}
                            placeholder="Informe um serviço por linha"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
                  {!isEditing ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      sx={{ borderColor: '#C23535', color: '#C23535', borderRadius: 2, fontWeight: 700, py: 1.4 }}
                    >
                      Editar Perfil
                    </Button>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<Save />}
                        onClick={() => formik.handleSubmit()}
                        disabled={state.loading}
                        sx={{ bgcolor: '#C23535', '&:hover': { bgcolor: '#A52A2A' }, borderRadius: 2, fontWeight: 700, py: 1.4 }}
                      >
                        {state.loading ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        sx={{ borderColor: '#C23535', color: '#C23535', borderRadius: 2, fontWeight: 700, py: 1.4 }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  )}
                </Grid>

              </Grid>
            </Box>
          </TabPanel>
        )}

        {isEmpresa && (
          <TabPanel value={tabValue} index={2}>
            <Card sx={cardSx}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Notifications sx={{ color: '#C23535' }} />
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535' }}>Mensagens da IDEBRASIL</Typography>
                </Box>
                {comunicacoes.length === 0 ? <Typography color="text.secondary">Você não possui mensagens no momento.</Typography> : comunicacoes.map((comunicacao) => (
                  <Paper key={comunicacao.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderLeft: `4px solid ${comunicacao.status === 'enviado' ? '#C23535' : '#2E7D32'}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                      <Box><Typography fontWeight={700}>{comunicacao.assunto}</Typography><Typography variant="caption" color="text.secondary">{new Date(comunicacao.enviado_em).toLocaleString('pt-BR')}</Typography></Box>
                      {comunicacao.status === 'enviado' && <Button size="small" variant="outlined" onClick={() => marcarComunicacaoComoLida(comunicacao.id)}>Marcar como lida</Button>}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{comunicacao.mensagem}</Typography>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </TabPanel>
        )}

        {/* ── ABA: Segurança ── */}
        <TabPanel value={tabValue} index={securityTabIndex}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={cardSx}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 1 }}>
                    Alterar Senha
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Use uma senha forte com pelo menos 6 caracteres.
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<VpnKey />}
                    onClick={() => {
                      setShowPasswordSection(v => !v);
                      setPasswordError('');
                      setPasswordSuccess('');
                      passwordFormik.resetForm();
                    }}
                    color={showPasswordSection ? 'error' : 'primary'}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    {showPasswordSection ? 'Cancelar' : 'Alterar Senha'}
                  </Button>

                  <Collapse in={showPasswordSection}>
                    <Box component="form" onSubmit={passwordFormik.handleSubmit} sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }} />
                      {passwordError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>{passwordError}</Alert>
                      )}
                      {passwordSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" id="senha_atual" name="senha_atual" label="Senha Atual"
                            type={showCurrentPwd ? 'text' : 'password'}
                            value={passwordFormik.values.senha_atual}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            error={passwordFormik.touched.senha_atual && Boolean(passwordFormik.errors.senha_atual)}
                            helperText={passwordFormik.touched.senha_atual && passwordFormik.errors.senha_atual}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowCurrentPwd(v => !v)} edge="end">
                                    {showCurrentPwd ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small" id="nova_senha" name="nova_senha" label="Nova Senha"
                            type={showNewPwd ? 'text' : 'password'}
                            value={passwordFormik.values.nova_senha}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            error={passwordFormik.touched.nova_senha && Boolean(passwordFormik.errors.nova_senha)}
                            helperText={passwordFormik.touched.nova_senha && passwordFormik.errors.nova_senha}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowNewPwd(v => !v)} edge="end">
                                    {showNewPwd ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth size="small" id="confirmar_senha" name="confirmar_senha" label="Confirmar Nova Senha"
                            type={showConfirmPwd ? 'text' : 'password'}
                            value={passwordFormik.values.confirmar_senha}
                            onChange={passwordFormik.handleChange}
                            onBlur={passwordFormik.handleBlur}
                            error={passwordFormik.touched.confirmar_senha && Boolean(passwordFormik.errors.confirmar_senha)}
                            helperText={passwordFormik.touched.confirmar_senha && passwordFormik.errors.confirmar_senha}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowConfirmPwd(v => !v)} edge="end">
                                    {showConfirmPwd ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={<Save />}
                            disabled={passwordFormik.isSubmitting}
                            sx={{ bgcolor: '#C23535', '&:hover': { bgcolor: '#A52A2A' }, borderRadius: 2, fontWeight: 700 }}
                          >
                            {passwordFormik.isSubmitting ? 'Salvando...' : 'Salvar Nova Senha'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ ...cardSx, borderColor: 'rgba(194, 53, 53, 0.3)' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#C23535', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteForever /> Privacidade e Exclusão de Conta
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                    Em conformidade com as diretrizes da Apple App Store, Google Play Store e LGPD, você pode encerrar sua conta e solicitar a remoção permanente de todos os seus dados pessoais e da sua empresa.
                  </Typography>

                  {deleteError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>{deleteError}</Alert>
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForever />}
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    Excluir Minha Conta e Dados
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Dialog de Confirmação de Exclusão */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
          >
            <DialogTitle sx={{ fontWeight: 700, color: '#C23535' }}>
              Confirmar Exclusão de Conta?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Esta ação é irreversível. Ao confirmar, seu acesso será desativado e o perfil da sua empresa será removido do catálogo de parceiros do IDECONECTA.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteAccount}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                startIcon={deleteLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteForever />}
              >
                {deleteLoading ? 'Excluindo...' : 'Sim, Excluir Definitivamente'}
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default UserProfile;
